/**
 * ConceptPicker — 개념 다중 선택(칩 + 검색 드롭다운).
 *
 * AI 생성 요청(⑥ 출제 개념)과 검수 카드(개념 교정)가 같은 조작을 쓴다. 2026-09-13 부터 OCR 검수 창도 이것을 쓴다 —
 * 오너 결정 D4 「개념 고르기는 이 부품이 정본」. 그 창이 제 손으로 들고 있던 동작 셋을 여기로 옮겼다:
 *   · ESC — 검색어가 있으면 비우고 거기서 멈춘다(창 닫기로 새지 않게). 비어 있으면 그대로 흘려보낸다.
 *   · Enter — 후보가 하나로 좁혀졌으면 바로 넣는다(번호를 아는 사람의 속도). 한글 조합 중의 Enter 는 넘긴다.
 *   · onChipClick — 칩을 누르면 개념 설명을 연다(검수 창의 useMathceptDetail).
 * 그리고 한 부품이 여러 문제를 도는 자리(검수 창)를 위해 둘을 더했다:
 *   · resetKey — 바뀌면 검색어를 비운다(앞 문제에서 치던 글자로 다음 문제의 목록이 열려 있지 않게)
 *   · busy — 저장 중에는 잠그되 칸은 그대로 세운다(disabled 는 입력칸째 걷는다 — 읽기 전용 자리용)
 * 2026-09-15 — 입력칸·목록 밖을 누르면 검색 목록을 닫는다(오너 「다른 영역을 선택하면 사라져야」, 쓰는 다섯 자리 모두). 검색어는 남는다.
 *
 * 개념 사전은 **v2**(`fetchConceptDictV2` → GET /api/v2/mathcepts, 계약 §8)를 스스로 부른다.
 * 5분 모듈 캐시라 검색 패널·시험지 화면이 먼저 받아 두었으면 여기서는 왕복이 없다.
 * 2026-09-01 까지는 v1(`utils/mathConceptsUtils` → /api/mathcepts/temp)을 썼다 — 문제(/problem-hub)
 * 화면에서 v1 을 치는 마지막 두 자리(여기·AnalysisReviewCard)였고, 오너 지시로 걷었다.
 * v1 을 다시 끌어오지 마라(conceptBadge.jsx 사전 주석 · 오너 방침 2026-08-26).
 */
import React, {
  useState, useEffect, useMemo, useRef,
} from 'react';
import { ConceptChip } from '../common';
import { useAuth } from '../../context/AuthContext';
import { fetchConceptDictV2 } from '../ExamPapersV2/conceptBadge';

function ConceptPicker({
  value = [],
  onChange,
  disabled = false,
  busy = false,
  emptyText = '개념을 고르세요',
  placeholder = '개념 검색 — 이름 · 번호 · ID',
  names = null, // 서버가 준 이름 맵(사전에 없는 개념도 이름을 보이게)
  onChipClick = null,
  resetKey = undefined,
  // 검색 결과를 흐름 안에 둔다(아래 줄을 밀어낸다) — 스크롤 칸 안에서 뒤에 다른 칸이 붙는 자리(검수 창)용.
  // 실측 2026-09-13: 같은 모양의 absolute z-10 목록(LabelEditor)이 스크롤 칸 안에서 뒤따르는 구역 밑에 깔려
  // 눌리지 않았다(z 를 올려도 그대로). 기본은 종전대로 띄운다 — 지금 쓰는 세 자리의 동작을 바꾸지 않는다.
  listInFlow = false,
  // 폐기(is_outdated) 개념을 검색 결과에서 뺀다 — 은행 문제 수정이 폐기 개념을 400 으로 거절하는 자리(문제 편집)용.
  // 이미 붙은 칩은 그대로 둔다(뗄 수 있어야 한다). 기본은 종전대로 전부 보인다.
  excludeOutdated = false,
  // 입력칸·목록 밖을 누르면 검색 목록을 닫는다 — 검색어는 두고, 입력칸을 다시 누르거나 치면 다시 연다.
  // 처음(2026-09-15 오전)엔 강의 조건 편집기만 켰다가 같은 날 오너 지시로 기본을 켰다. false 면 검색어가 있는 동안 열어 둔다
  closeOnOutside = true,
  // 검색 칸을 강조한다 — 고른 개념이 없어 여기서 골라야 할 때(강의 조건 편집기, 오너 2026-09-15 「입력박스에 하이라이팅」). 기본은 종전대로
  highlight = false,
}) {
  const { getAccessToken } = useAuth();
  const [concepts, setConcepts] = useState([]);
  const [query, setQuery] = useState('');
  const [listOpen, setListOpen] = useState(true);
  const searchRef = useRef(null);

  useEffect(() => {
    if (!closeOnOutside) return undefined;
    const onDown = (e) => {
      const box = searchRef.current;
      if (box && e.target instanceof Node && !box.contains(e.target)) setListOpen(false);
    };
    // 문서 맨 위에서 먼저 듣는다(capture) — 이 부품을 품은 창이 누르기 전달을 막아도(stopPropagation) 닫히게
    document.addEventListener('mousedown', onDown, true);
    return () => document.removeEventListener('mousedown', onDown, true);
  }, [closeOnOutside]);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  // fetchConceptDictV2 는 실패하면 null 을 돌려준다(던지지 않는다). null 도 빈 배열과 같이
  // 「불러오지 못했습니다 · 다시 시도」로 세운다 — 사전이 717개라 진짜 0건은 없다.
  // getAccessToken 은 AuthContext 의 useCallback 이라 참조가 고정이다(effect 가 돌지 않는다).
  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.resolve(fetchConceptDictV2(getAccessToken))
      .then((list) => { if (alive) setConcepts(list || []); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [reloadKey, getAccessToken]);

  // 다른 문제로 넘어갔다 — 검색어를 비운다. 부품을 다시 세우지(key) 않는 이유: 다시 세우면 사전을
  // 다시 읽는 한 박자 동안 입력칸이 잠겼다 풀리며 깜빡인다.
  useEffect(() => { setQuery(''); }, [resetKey]);

  const byId = useMemo(() => {
    const m = new Map();
    concepts.forEach((c) => m.set(String(c.id), c));
    return m;
  }, [concepts]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return concepts
      .filter((c) => !value.includes(c.id))
      .filter((c) => !(excludeOutdated && c.is_outdated))
      // 이름 필드는 ConceptChip 과 같은 순서로 읽는다 — v2 사전은 concept_name, 폴백 객체는 text
      .filter((c) => String(c.id).includes(q)
        || String(c.concept_name || c.text || '').toLowerCase().includes(q)
        || String(c.order || '').toLowerCase().includes(q))
      .slice(0, 30);
  }, [concepts, query, value, excludeOutdated]);

  const showList = query.trim() !== '' && listOpen;
  const listRef = useRef(null);
  // 흐름 안 목록이 열리는 순간 보이는 자리로 한 번 — 입력칸이 스크롤 칸 바닥에 있으면 목록이 칸 밖으로 밀려
  // 「쳐도 안 뜬다」로 보인다(2026-09-15 AI 문제 생성 창의 변형 검수 카드 실측). 이미 보이면 움직이지 않는다(nearest)
  useEffect(() => {
    if (showList && listInFlow) listRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [showList, listInFlow]);

  const add = (id) => {
    if (busy) return;
    if (!value.includes(id)) onChange([...value, id]);
    setQuery('');
  };
  const remove = (id) => onChange(value.filter((v) => v !== id));

  return (
    <div className="space-y-1.5">
      <div className="flex min-h-8 flex-wrap items-center gap-1 rounded-lg border border-gray-200 bg-white p-1.5">
        {value.map((cid) => (
          <ConceptChip
            key={cid}
            concept={byId.get(String(cid)) || { id: cid, text: names?.(cid) || `#${cid}` }}
            onRemove={disabled ? undefined : remove}
            onClick={onChipClick || undefined}
            disabled={busy}
          />
        ))}
        {value.length === 0 && <span className="text-xs text-gray-400">{emptyText}</span>}
      </div>
      {!disabled && !loading && concepts.length === 0 && (
        <p className="flex items-center gap-1.5 text-xs text-amber-600">
          개념 사전을 불러오지 못했습니다.
          <button type="button" onClick={() => setReloadKey((k) => k + 1)} className="underline hover:text-amber-700">
            다시 시도
          </button>
        </p>
      )}
      {!disabled && (
        <div ref={searchRef} className="relative">
          <input
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setListOpen(true); }}
            onFocus={() => setListOpen(true)}
            onKeyDown={(e) => {
              if (e.key === 'Escape' && query !== '') { setQuery(''); e.stopPropagation(); return; }
              if (e.key === 'Enter' && !e.nativeEvent.isComposing && matches.length === 1) {
                e.preventDefault();
                add(matches[0].id);
              }
            }}
            aria-label="개념 검색"
            placeholder={loading ? '개념 사전을 읽는 중…' : placeholder}
            disabled={loading || busy}
            className={`w-full rounded-lg border px-2 py-1.5 text-sm focus:border-primary focus:outline-none disabled:bg-gray-50 ${highlight ? 'border-primary ring-2 ring-primary/30' : ''}`}
          />
          {showList && (
            <div ref={listRef} className={`${listInFlow ? '' : 'absolute inset-x-0 z-10 '}mt-1 max-h-44 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-sm`}>
              {matches.length === 0 ? (
                <div className="p-2 text-xs text-gray-400">찾는 개념이 없습니다</div>
              ) : matches.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => add(c.id)}
                  disabled={busy}
                  className="flex w-full items-center gap-1.5 px-2 py-1 text-left transition-colors hover:bg-primary/10 disabled:opacity-40"
                >
                  <ConceptChip concept={c} />
                  <span className="ml-auto shrink-0 font-mono text-[10px] text-gray-400">#{c.id}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ConceptPicker;
